import {
    faBan,
    faEdit,
    faFileExport,
    faFileImport,
    faFileInvoice,
    faGift,
    faLink,
    faList,
    faNewspaper,
    faPlus,
    faRedo,
    faRetweet,
    faTrash,
    faUndo,
    faUserCheck,
    faUserXmark
} from '@fortawesome/free-solid-svg-icons';
import { useApi } from '@hooks/useApi';
import { useConfirmedCallback } from '@hooks/useConfirmedCallback';
import useFileDownload from '@hooks/useFileDownload';
import useModalManager from '@hooks/useModalManager';
import { Blockquote, Button } from '@ui';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import { MultiSubscribeToNewsletter } from '~/components/Newsletter/MultiSubscribeToNewsletter';
import { useClubSlug } from '../../Clubs/hooks';
import { Action, Column, DataTable, DataTableContainer, Filter, TableActionButton } from '../../DataTable';
import { ActionCallbackProps, AsyncListRef } from '../../DataTable/DataTable';
import Loader from '../../Loader';
import { UserInvoice } from '../../UserInvoice/types';
import useAttendanceConfirmation from '../Attendance/useAttendanceConfirmation';
import { Allocation, AllocationGroup, CompactAllocation } from '../types';
import AllocationMeta from './AllocationMeta';
import AllocationPrice from './AllocationPrice';
import AllocationRebookingLinkForm from './AllocationRebookingLinkForm';
import Bookers from './Bookers';

const AllocationFormWithSchedule = dynamic(() => import('../AllocationForm/AllocationFormWithSchedule'));
const AllocationsForm = dynamic(() => import('../AllocationsForm'));
const AllocationCancellationForm = dynamic(() => import('../AllocationCancellationForm'));
const AllocationInvoiceForm = dynamic(() => import('../AllocationInvoiceForm'));
const AllocationVoucherForm = dynamic(() => import('../AllocationVoucherForm'));
const RecurringAllocationList = dynamic(() => import('../RecurringAllocationList'));
const ImportPickerModal = dynamic(() => import('./ImportPickerModal'));

interface Props {
    recurringId?: string;
    className?: string;
    latest?: boolean;
}

const AllocationList = ({ recurringId, className, latest }: Props) => {
    const clubSlug = useClubSlug();
    const api = useApi();
    const { t } = useTranslation('space-manager');
    const listRef = useRef<AsyncListRef>(undefined);
    const download = useFileDownload();
    const [actionsLoading, setActionsLoading] = useState(false);
    const { data, isLoading } = useSWR<{ data: AllocationGroup[] }>(`club/${clubSlug}/space-manager/allocation-group`);
    const { mutate } = useSWRConfig();
    const { addModal, resetModals } = useModalManager();
    const {
        confirm: confirmAttendance,
        isLoading: attendanceIsLoading,
        canConfirm: canConfirmAttendance
    } = useAttendanceConfirmation();

    const handleRefreshAllocations = () =>
        void mutate((key) => typeof key === 'string' && key.startsWith(`club/${clubSlug}/space-manager/space/grid`));

    const columns = useMemo(
        (): Column<CompactAllocation>[] => [
            {
                uid: 'meta',
                name: t('allocationList.columns.meta'),
                renderer: (_, item) => <AllocationMeta allocation={item} />
            },
            {
                uid: 'bookers',
                name: t('allocationList.columns.bookers'),
                align: 'center',
                renderer: (_, item) => <Bookers allocation={item} />
            },
            {
                uid: 'price',
                name: t('allocationList.columns.price'),
                align: 'center',
                renderer: (_, item) => <AllocationPrice allocation={item} />
            }
        ],
        [t]
    );

    const handleExport = ({ selection, filter, setSelection }: ActionCallbackProps) => {
        setActionsLoading(true);
        void api({
            url: `club/${clubSlug}/space-manager/allocation/export`,
            data: { selection, filter },
            onBefore: () => setActionsLoading(false),
            onSuccess: (data) => {
                download(data.download_id);
                setSelection([]);
            }
        });
    };

    const handleExportBooker = ({ selection, filter, setSelection }: ActionCallbackProps) => {
        setActionsLoading(true);
        void api({
            url: `club/${clubSlug}/space-manager/allocation/export-booker`,
            data: { selection, filter },
            onBefore: () => setActionsLoading(false),
            onSuccess: (data) => {
                download(data.download_id);
                setSelection([]);
            }
        });
    };

    const showNewsletterForm = ({ selection, filter }: ActionCallbackProps) =>
        addModal({
            title: t('allocationList.modals.importMembers'),
            content: (
                <MultiSubscribeToNewsletter
                    filter={filter}
                    clubSlug={clubSlug}
                    selection={selection}
                    variant="allocation"
                    callback={() => {
                        resetModals();
                        listRef.current.reload();
                    }}
                />
            ),
            show: true,
            size: 50
        });

    const handleEditMultiple = ({ selection, filter, setSelection }: ActionCallbackProps) => {
        addModal({
            title: t('allocationList.modals.editMultiple'),
            content: (
                <AllocationsForm
                    selection={selection}
                    filter={{ ...filter, ...(recurringId ? { recurring_id: { value: recurringId } } : {}) }}
                    recurringId={recurringId}
                    callback={() => {
                        listRef.current.reload();
                        setSelection([]);
                        handleRefreshAllocations();
                        resetModals();
                    }}
                />
            ),
            show: true,
            size: 60
        });
    };

    // Opens the bulk editor scoped to a whole recurring series instead of a manual selection
    const handleEditSeries = useCallback(
        (seriesId: string) => {
            addModal({
                title: t('allocationList.modals.editSeries'),
                content: (
                    <AllocationsForm
                        selection="all"
                        filter={{ recurring_id: { value: seriesId } }}
                        recurringId={seriesId}
                        callback={() => {
                            listRef.current?.reload();
                            handleRefreshAllocations();
                            resetModals();
                        }}
                    />
                ),
                show: true,
                size: 60
            });
        },
        [addModal, resetModals, t]
    );

    // Opens the import chooser. Deliberately independent of the table selection: an import
    // brings its own rows along and matches them against the club's bookings itself.
    const handleShowImportPicker = useCallback(() => {
        addModal({
            title: t('allocationList.modals.import'),
            content: (
                <ImportPickerModal
                    callback={() => {
                        resetModals();
                        listRef.current?.reload();
                    }}
                />
            ),
            show: true,
            size: 60
        });
    }, [addModal, resetModals, t]);

    const handleShowRecurringAllocations = useCallback(() => {
        addModal({
            title: t('allocationList.modals.recurringAllocations'),
            // Feste Höhe: erst dadurch ist die Tabelle nach unten begrenzt und scrollt
            // selbst, statt das Modal immer weiter wachsen zu lassen (dann lädt sie nicht nach).
            content: (
                <div className="tw-flex tw-h-[78vh] tw-flex-col">
                    <RecurringAllocationList />
                </div>
            ),
            show: true,
            size: 70
        });
    }, [addModal, t]);

    const handleDeleteMultiple = ({ selection, filter, setSelection }: ActionCallbackProps) => {
        setActionsLoading(true);
        void api({
            url: `club/${clubSlug}/space-manager/allocation`,
            method: 'DELETE',
            data: { selection, filter: { ...filter, recurring_id: { value: recurringId } } },
            onBefore: () => setActionsLoading(false),
            toastCallback: () => {
                listRef.current.reload();
                handleRefreshAllocations();
                setSelection([]);
            }
        });
    };

    const handleCancelMultiple = ({ selection, filter, setSelection }: ActionCallbackProps) => {
        addModal({
            title: t('allocationList.modals.cancelMultiple'),
            content: (
                <AllocationCancellationForm
                    selection={selection}
                    filter={{ ...filter, recurring_id: { value: recurringId } }}
                    callback={() => {
                        listRef.current.reload();
                        setSelection([]);
                        handleRefreshAllocations();
                        resetModals();
                    }}
                />
            ),
            show: true
        });
    };

    const handleOpenAllocationForm = useCallback(
        (allocation?: CompactAllocation) => {
            addModal({
                title: allocation
                    ? t('allocationList.modals.editAllocation')
                    : t('allocationList.modals.createAllocation'),
                content: (
                    <AllocationFormWithSchedule
                        allocation={allocation}
                        callback={(newAllocation: Allocation | Allocation[]) => {
                            if (!allocation)
                                if (
                                    listRef.current.sortDescriptor?.column === 'created_at' ||
                                    listRef.current.sortDescriptor?.column === undefined
                                )
                                    listRef.current?.prepend(
                                        ...(Array.isArray(newAllocation) ? newAllocation : [newAllocation])
                                    );
                                else listRef.current?.reload();
                            else listRef.current?.update(allocation?.id, newAllocation);

                            resetModals();
                        }}
                    />
                ),
                show: true,
                size: 85
            });
        },
        [addModal, resetModals, t]
    );

    const handleCancel = (allocation: CompactAllocation) => {
        addModal({
            title: t('allocationList.modals.cancelAllocation'),
            content: (
                <AllocationCancellationForm
                    allocation={allocation}
                    callback={() => {
                        // reload instead of update: the cancel response is an AllocationResource
                        // and carries neither cancelled_at nor the list-only columns, so the row
                        // would lose them and the un-cancel button would never show up
                        listRef.current?.reload();
                        handleRefreshAllocations();
                        resetModals();
                    }}
                />
            ),
            show: true
        });
    };

    // reload instead of update: the row keeps its cancellation columns and the grid has to learn
    // that the slot is occupied again
    const handleUncancel = useConfirmedCallback(
        (allocation: CompactAllocation) =>
            void api({
                url: `space-manager/allocation/${allocation.id}/cancel`,
                method: 'DELETE',
                onSuccess: () => {
                    listRef.current?.reload();
                    handleRefreshAllocations();
                }
            }),
        {
            header: t('allocationList.confirm.uncancelHeader'),
            message: t('allocationList.confirm.uncancelAllocation')
        }
    );

    const handleDelete = useConfirmedCallback((allocation?: CompactAllocation) => {
        void api({
            url: `space-manager/allocation/${allocation.id}`,
            method: 'DELETE',
            onSuccess: () => listRef.current?.remove(allocation.id)
        });
    });

    // reload instead of update: confirming may revive a booking the prune job cancelled, which
    // changes status and cancellation reason as well — and the grid has to learn about it
    const handleAttendanceChanged = () => {
        listRef.current?.reload();
        handleRefreshAllocations();
    };

    // confirms the attendance for every booker of the booking at once — the counter variant of
    // the code the bookers received
    const handleConfirmAttendance = useConfirmedCallback(
        (allocation: CompactAllocation) =>
            void confirmAttendance({ allocationId: allocation.id, verified: true }, handleAttendanceChanged),
        { message: t('allocationList.confirm.confirmAttendance') }
    );

    // withdrawing after the deadline hands the booking straight back to the prune job, so the
    // dialog says so instead of asking the generic question
    const handleWithdrawAttendance = useConfirmedCallback(
        (allocation: CompactAllocation) =>
            void confirmAttendance({ allocationId: allocation.id, verified: false }, handleAttendanceChanged),
        { message: t('allocationList.confirm.withdrawAttendance') }
    );

    const handleCreateRebookingLink = useCallback(
        (allocation: CompactAllocation) => {
            addModal({
                title: t('allocationList.modals.createRebookingLink'),
                content: (
                    <AllocationRebookingLinkForm
                        allocation={allocation}
                        callback={async (data) => {
                            resetModals();

                            const fallbackMessage = t('allocationList.rebookingLinkCreatedFallback', {
                                link: data.link
                            });

                            if (!navigator.clipboard) {
                                alert(fallbackMessage);
                                return;
                            }

                            try {
                                await navigator.clipboard.writeText(data.link);
                                alert(t('allocationList.rebookingLinkCreatedSuccess'));
                            } catch {
                                alert(fallbackMessage);
                            }
                        }}
                    />
                ),
                show: true,
                size: 40
            });
        },
        [addModal, resetModals, t]
    );

    // Open modal to create vouchers for selected bookers of this allocation
    const handleCreateVoucher = useCallback(
        (allocation: CompactAllocation) => {
            addModal({
                title: t('allocationList.modals.createVoucher'),
                content: <AllocationVoucherForm allocation={allocation} callback={() => resetModals()} />,
                show: true,
                size: 40
            });
        },
        [addModal, resetModals, t]
    );

    const handleCreateRecurringInvoice = useConfirmedCallback(() => {
        setActionsLoading(true);
        void api({
            url: `club/${clubSlug}/space-manager/allocation/invoice/${recurringId}`,
            method: 'POST',
            onBefore: () => setActionsLoading(false),
            onSuccess: () => alert(t('allocationList.recurringInvoiceCreated'))
        });
    });

    const handleShowRecurringItems = (allocation: CompactAllocation) => {
        addModal({
            title: t('allocationList.modals.recurringItems'),
            content: (
                <div className="tw-flex tw-h-[70vh] tw-flex-col">
                    <AllocationList recurringId={allocation.recurring_id} />
                </div>
            ),
            show: true
        });
    };

    const handleOpenAllocationInvoiceForm =
        (update: boolean) =>
        ({ selection, filter, setSelection }: ActionCallbackProps) => {
            addModal({
                title: t('allocationList.modals.createOrUpdateInvoice'),
                content: (
                    <AllocationInvoiceForm
                        selection={selection}
                        update={update}
                        filter={filter}
                        callback={() => {
                            listRef.current.reload();
                            setSelection([]);
                            resetModals();
                        }}
                    />
                ),
                show: true
            });
        };

    const actions = useMemo(
        (): Action[] => [
            {
                key: 'create',
                action: () => handleOpenAllocationForm(),
                label: t('allocationList.actions.create'),
                icon: faPlus
            },
            { key: 'export-csv', action: handleExport, label: t('allocationList.actions.export'), icon: faFileExport },
            {
                key: 'export-booker-csv',
                action: handleExportBooker,
                label: t('allocationList.actions.exportBooker'),
                icon: faFileExport
            },
            {
                key: 'import',
                action: handleShowImportPicker,
                label: t('allocationList.actions.import'),
                icon: faFileImport
            },
            {
                key: 'create-invoices',
                action: handleOpenAllocationInvoiceForm(false),
                label: t('allocationList.actions.createInvoices'),
                icon: faFileInvoice
            },
            {
                key: 'update-invoices',
                action: handleOpenAllocationInvoiceForm(true),
                label: t('allocationList.actions.updateInvoices'),
                icon: faRetweet
            },
            {
                key: 'recurring-allocations',
                action: handleShowRecurringAllocations,
                label: t('allocationList.actions.showRecurring'),
                icon: faRedo
            },
            {
                key: 'edit-multiple',
                action: handleEditMultiple,
                label: t('allocationList.actions.editMultiple'),
                icon: faEdit,
                color: 'warning'
            },
            {
                icon: faNewspaper,
                key: 'add-newsletter',
                label: t('allocationList.actions.addNewsletter'),
                action: showNewsletterForm
            },
            {
                key: 'delete-multiple',
                action: handleDeleteMultiple,
                label: t('allocationList.actions.deleteMultiple'),
                icon: faTrash,
                color: 'error'
            },
            {
                key: 'cancel-multiple',
                action: handleCancelMultiple,
                label: t('allocationList.actions.cancelMultiple'),
                icon: faBan,
                color: 'error'
            }
        ],
        [t]
    );

    const handleShowPDF = (invoice: UserInvoice) => {
        let popup = window.open(invoice.url, '_blank');
        if (popup != null) popup.focus();
    };

    const filters: Filter[] = useMemo(
        () => [
            { filterName: t('allocationList.filters.bookedAt'), filterKey: 'created_at', filterType: 'date' },
            {
                filterName: t('allocationList.filters.cancelled'),
                filterKey: 'cancelled',
                filterType: 'select',
                options: [
                    { label: t('allocationList.filters.yes'), value: String(true) },
                    { label: t('allocationList.filters.no'), value: String(false) }
                ]
            },
            {
                filterName: t('allocationList.filters.freeAllocation'),
                filterKey: 'free_allocations',
                filterType: 'select',
                options: [
                    { label: t('allocationList.filters.yes'), value: String(true) },
                    { label: t('allocationList.filters.no'), value: String(false) }
                ]
            },
            {
                filterName: t('allocationList.filters.fromSubscriptionTemplate'),
                filterKey: 'has_subscription_template',
                filterType: 'select',
                options: [
                    { label: t('allocationList.filters.yes'), value: String(true) },
                    { label: t('allocationList.filters.no'), value: String(false) }
                ]
            },
            {
                filterName: t('allocationList.filters.price'),
                filterKey: 'price',
                filterType: 'numeric'
            },
            { filterName: t('allocationList.filters.cancelledAt'), filterKey: 'cancelled_at', filterType: 'date' },
            { filterName: t('allocationList.filters.updatedAt'), filterKey: 'updated_at', filterType: 'date' },
            { filterName: t('allocationList.filters.date'), filterKey: 'start', filterType: 'date' },
            { filterName: t('allocationList.filters.bookersCount'), filterKey: 'bookers_count', filterType: 'numeric' },
            { filterName: t('allocationList.filters.bookingId'), filterKey: 'id', filterType: 'text' },
            { filterName: t('allocationList.filters.bookerEmail'), filterKey: 'booker_email', filterType: 'text' },
            { filterName: t('allocationList.filters.bookerName'), filterKey: 'booker_name', filterType: 'text' },
            {
                filterName: t('allocationList.filters.guestBookings'),
                filterKey: 'guest_bookings',
                filterType: 'select',
                options: [
                    { label: t('allocationList.filters.yes'), value: String(true) },
                    { label: t('allocationList.filters.no'), value: String(false) }
                ]
            },
            {
                filterName: t('allocationList.filters.attendance'),
                filterKey: 'attendance',
                filterType: 'select',
                options: [
                    { label: t('allocationList.filters.attendanceRequired'), value: 'required' },
                    { label: t('allocationList.filters.attendanceOpen'), value: 'open' },
                    { label: t('allocationList.filters.attendanceVerified'), value: 'verified' }
                ]
            },
            {
                filterName: t('allocationList.filters.allocationGroup'),
                filterKey: 'allocation_group_id',
                options: [
                    // Sentinel-Wert, den AllocationFilter::allocation_group_id() als whereNull auswertet
                    { label: t('allocationList.filters.withoutAllocationGroup'), value: 'none' },
                    ...(data?.data?.map((g) => ({
                        label: g.name,
                        value: g.id
                    })) ?? [])
                ],
                filterType: 'select'
            }
        ],
        [t, data]
    );

    return (
        <Loader isLoading={isLoading}>
            <DataTableContainer className={className}>
                {!!recurringId && (
                    <>
                        <Blockquote>{t('allocationList.recurringInvoiceBlockquote')}</Blockquote>
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                            <Button isLoading={actionsLoading} color="secondary" onPress={handleCreateRecurringInvoice}>
                                {t('allocationList.createRecurringInvoiceButton')}
                            </Button>
                            <Button color="warning" onPress={() => handleEditSeries(recurringId)}>
                                {t('allocationList.editSeriesButton')}
                            </Button>
                        </div>
                    </>
                )}
                <DataTable
                    initialFilterState={recurringId ? { recurring_id: { value: recurringId } } : undefined}
                    actionsLoading={actionsLoading || attendanceIsLoading}
                    showStatistic={!latest}
                    asyncListRef={listRef}
                    hideSearchBar={latest}
                    actionsLabel={t('allocationList.actionsLabel')}
                    tableProps={{
                        showSelectionCheckboxes: true,
                        selectionMode: 'multiple',
                        sticked: true,
                        bordered: true,
                        borderWeight: 'light',
                        compact: true
                    }}
                    filters={filters}
                    fetchUrl={`club/${clubSlug}/space-manager/allocation${latest ? '/latest' : ''}`}
                    columns={columns}
                    actions={actions}
                    rowActions={(item: CompactAllocation) => (
                        <TableActionButton.Container>
                            {item.invoices?.length > 0 && (
                                <TableActionButton
                                    icon={faFileInvoice}
                                    color="secondary"
                                    tooltip={t('allocationList.tooltips.showInvoice')}
                                    onPress={() => handleShowPDF(item.invoices?.[0])}
                                />
                            )}
                            {item.recurring_id && (
                                <TableActionButton
                                    icon={faList}
                                    color="secondary"
                                    tooltip={t('allocationList.tooltips.showRecurringItems')}
                                    onPress={() => handleShowRecurringItems(item)}
                                />
                            )}
                            <TableActionButton
                                icon={faLink}
                                color="secondary"
                                tooltip={t('allocationList.tooltips.createRebookingLink')}
                                onPress={() => handleCreateRebookingLink(item)}
                            />
                            <TableActionButton
                                icon={faGift}
                                color="secondary"
                                tooltip={t('allocationList.tooltips.createVoucher')}
                                onPress={() => handleCreateVoucher(item)}
                            />
                            <TableActionButton
                                icon={faEdit}
                                color="warning"
                                tooltip={t('allocationList.tooltips.editAllocation')}
                                onPress={() => handleOpenAllocationForm(item)}
                            />
                            {item.recurring_id && (
                                <TableActionButton
                                    icon={faRedo}
                                    color="warning"
                                    tooltip={t('allocationList.tooltips.editSeries')}
                                    onPress={() => handleEditSeries(item.recurring_id)}
                                />
                            )}
                            {/* only bookings the VerifyAttendance effect applies to, and only for
                                admins who may decide over bookings */}
                            {!!item.attendance && canConfirmAttendance && (
                                <TableActionButton
                                    icon={item.attendance.fulfilled ? faUserXmark : faUserCheck}
                                    color={item.attendance.fulfilled ? 'warning' : 'success'}
                                    tooltip={
                                        item.attendance.fulfilled
                                            ? t('allocationList.tooltips.withdrawAttendance')
                                            : t('allocationList.tooltips.confirmAttendance')
                                    }
                                    onPress={() =>
                                        item.attendance.fulfilled
                                            ? handleWithdrawAttendance(item)
                                            : handleConfirmAttendance(item)
                                    }
                                />
                            )}
                            <TableActionButton
                                icon={faTrash}
                                color="error"
                                tooltip={t('allocationList.tooltips.deleteAllocation')}
                                onPress={() => handleDelete(item)}
                            />
                            {!item.cancelled_at && (
                                <TableActionButton
                                    icon={faBan}
                                    color="error"
                                    tooltip={t('allocationList.tooltips.cancelAllocation')}
                                    onPress={() => handleCancel(item)}
                                />
                            )}
                            {/* declined or expired requests also carry a cancelled_at, but they
                                were never a booking and cannot be revived */}
                            {!!item.cancelled_at && item.status === 'cancelled' && (
                                <TableActionButton
                                    icon={faUndo}
                                    color="error"
                                    tooltip={t('allocationList.tooltips.uncancelAllocation')}
                                    onPress={() => handleUncancel(item)}
                                />
                            )}
                        </TableActionButton.Container>
                    )}
                />
            </DataTableContainer>
        </Loader>
    );
};

export default AllocationList;
